import { useCallback, useEffect, useRef, useState } from "react";
import {
  FieldErrors,
  collectFieldErrors,
  focusFirstError,
} from "../render/collectFieldErrors";
import { mergeRuleErrors } from "../render/collectRuleErrors";

// Shared, so a caller with no validators doesn't hand the effect a new object each render.
const NoValidators: Record<string, string> = {};

/** The form's field errors: none until a save is attempted, then kept up to date. */
export function useEntityPageValidation({
  fieldLabels,
  fieldValidatorKeys = NoValidators,
  record,
  id,
  formId,
  stepOnly = false,
}: {
  fieldLabels: Record<string, string>;
  /** The validator each field names, by field name. */
  fieldValidatorKeys?: Record<string, string>;
  record: Record<string, unknown> | null;
  id: string | null;
  formId: string;
  /** Only the fields in ieldLabels count, for a wizard step that is not the whole form. */
  stepOnly?: boolean;
}) {
  const formRef = useRef<HTMLFormElement>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  // Errors are shown from the first save attempt onwards, not before it.
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [validationAttempts, setValidationAttempts] = useState(0);

  const collect = useCallback(
    () =>
      mergeRuleErrors(
        stepOnly
          ? Object.fromEntries(
              Object.entries(collectFieldErrors(formRef.current, fieldLabels)).filter(
                ([name]) => name in fieldLabels
              )
            )
          : collectFieldErrors(formRef.current, fieldLabels),
        record,
        fieldValidatorKeys,
        Object.keys(fieldLabels)
      ),
    [fieldLabels, fieldValidatorKeys, record, stepOnly]
  );

  // Errors appear on the first save attempt and then track what the user types, so a
  // field clears the moment it is fixed rather than at the next attempt.
  useEffect(() => {
    if (!validationAttempted) {
      return;
    }

    setFieldErrors(collect());
  }, [validationAttempted, collect]);

  // A different record, or a different view of one, is a fresh start.
  useEffect(() => {
    setValidationAttempted(false);
    setFieldErrors({});
  }, [id, formId]);

  /** Shows the form's errors and focuses the first; true when there are none. */
  const validate = useCallback(() => {
    // `noValidate` is on the form, so this is the whole of the user's feedback.
    const found = collect();

    setValidationAttempted(true);
    setValidationAttempts((count) => count + 1);
    setFieldErrors(found);

    if (Object.keys(found).length > 0) {
      focusFirstError(formRef.current, found);

      return false;
    }

    return true;
  }, [collect]);

  /** Back to showing nothing, as before a save was tried. */
  const resetValidation = useCallback(() => {
    setValidationAttempted(false);
    setFieldErrors({});
  }, []);

  return { formRef, fieldErrors, validationAttempts, validate, resetValidation };
}
